import Tabs from "../../General/ResponsiveTabs/ResponsiveTabs";
import React, { useMemo, useState } from "react";
import {
  Box,
  Tab,
  Typography,
  CircularProgress,
  Drawer,
  IconButton,
} from "@mui/material";
import { DataGrid, GridCellParams, GridColDef } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import CloseIcon from "@mui/icons-material/Close";
import useAuth from "../../../hooks/useAuth";
import { apiVariable } from "../../../api/apiVariable";
import DetailsList from "./DetailsList/DetailsList";

type AssetType = "truck" | "trailer";

const getCountsUrl = (asset: AssetType) =>
  `${apiVariable}/authority${
    asset === "truck" ? "Truck" : "Trailer"
  }TransactionCounts`;

const getDrillDownUrl = (asset: AssetType) =>
  `${apiVariable}/authority${
    asset === "truck" ? "Truck" : "Trailer"
  }TransactionDrillDown`;

async function fetchAuthorityTransactionCounts(
  token: string | null | undefined,
  asset: AssetType,
) {
  const res = await axios.get(getCountsUrl(asset), {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
}

function usePivotedCounts(raw: any[] | undefined) {
  return useMemo(() => {
    if (!raw || raw.length === 0) return { rows: [], columns: [] };

    const authorityNames = [
      ...new Set(raw.map((r) => (r.authorityCompanyName || "Unknown").trim())),
    ].sort();

    const map: Record<string, any> = {};
    raw.forEach((r) => {
      const key = r.transactionType;
      if (!map[key]) {
        map[key] = { id: key, transactionType: key, total: 0 };
        authorityNames.forEach((a) => (map[key][a] = 0));
      }
      map[key][r.authorityCompanyName] = r.count;
      map[key].total += r.count;
    });

    const totalsRow = authorityNames.reduce(
      (acc, authorityName) => {
        acc[authorityName] = raw
          .filter(
            (r) => (r.authorityCompanyName || "").trim() === authorityName,
          )
          .reduce((sum, r) => sum + (r.count || 0), 0);
        return acc;
      },
      { id: "totals", transactionType: "Total", total: 0 } as Record<
        string,
        any
      >,
    );

    totalsRow.total = authorityNames.reduce(
      (sum, authorityName) => sum + (totalsRow[authorityName] || 0),
      0,
    );

    const pivotColumns: GridColDef[] = [
      {
        field: "transactionType",
        headerName: "Transaction Type",
        width: 200,
        sortable: false,
      },
      ...authorityNames.map(
        (name): GridColDef => ({
          field: name,
          headerName: name,
          width: 200,
          align: "center",
          headerAlign: "center",
          sortable: false,
        }),
      ),
      {
        field: "total",
        headerName: "Total",
        width: 200,
        align: "center",
        headerAlign: "center",
        sortable: false,
      },
    ];

    return { rows: [...Object.values(map), totalsRow], columns: pivotColumns };
  }, [raw]);
}

interface AuthorityTransactionTableProps {
  containerHeight?: number;
  gridHeight?: number;
}

const AuthorityTransactionTable: React.FC<AuthorityTransactionTableProps> = ({
  containerHeight = 700,
  gridHeight = 500,
}) => {
  const { token } = useAuth();

  const [asset, setAsset] = useState<AssetType>("truck");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drillDownData, setDrillDownData] = useState<any>(null);
  const [isLoadingDrillDown, setIsLoadingDrillDown] = useState(false);
  const [drillDownError, setDrillDownError] = useState<string | null>(null);
  const [selectedAuthority, setSelectedAuthority] = useState<string | null>(
    null,
  );

  const {
    data: tableData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["authorityTransactionCounts", asset],
    queryFn: () => fetchAuthorityTransactionCounts(token, asset),
    enabled: !!token,
  });

  const { rows: pivotRows, columns: pivotColumns } =
    usePivotedCounts(tableData);

  const clickableCellStyle = {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    },
  };

  const columnsWithClickableCells = pivotColumns.map((col) => {
    if (col.field === "transactionType" || col.field === "total") {
      return col;
    }
    return {
      ...col,
      renderCell: (params: GridCellParams<any, any, any>) => (
        <Box sx={clickableCellStyle}>{String(params.value)}</Box>
      ),
    };
  });

  const handleTabChange = (_: unknown, newValue: AssetType) =>
    setAsset(newValue);

  const handleCellClick = async (params: GridCellParams) => {
    if (params.field === "transactionType" || params.field === "total") return;
    if ((params.row as any).id === "totals") return;

    if (!params.value) return;

    const transactionType = (params.row as any).transactionType;
    const authorityName = params.field;
    setSelectedAuthority(authorityName);
    setDrawerOpen(true);

    setIsLoadingDrillDown(true);
    setDrillDownError(null);
    setDrillDownData(null);

    try {
      let authorityCompanyId: string | null = null;

      if (authorityName !== "Others") {
        const match = (tableData as any[]).find(
          (r) =>
            r.transactionType === transactionType &&
            r.authorityCompanyName.trim() === authorityName.trim(),
        );
        authorityCompanyId = match?.authorityCompanyId ?? null;
      }

      const res = await axios.get(getDrillDownUrl(asset), {
        headers: { Authorization: `Bearer ${token}` },
        params: { authorityCompanyId, transactionType },
      });
      setDrillDownData(res.data);
    } catch (err: any) {
      setDrillDownError(err.message || "Error fetching drill-down data.");
    } finally {
      setIsLoadingDrillDown(false);
    }
  };

  const overallTotal = useMemo(
    () =>
      pivotRows
        .filter((row) => row.id !== "totals")
        .reduce((sum, row) => sum + (row.total || 0), 0),
    [pivotRows],
  );

  return (
    <Box sx={{ height: containerHeight }} className="maintenance-table">
      <Box sx={{ mb: 2 }}>
        <Tabs value={asset} onChange={handleTabChange}>
          <Tab value="truck" label="Trucks" />
          <Tab value="trailer" label="Trailers" />
        </Tabs>
        <Box
          sx={{
            mt: 1,
            display: "flex",
            justifyContent: "flex-start",
            pr: 2,
          }}
        >
          <Typography variant="subtitle1">
            Total: <strong>{overallTotal}</strong>
          </Typography>
        </Box>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center">
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Typography color="error">
          Error: {(error as Error).message || "Unknown Error"}
        </Typography>
      ) : (
        <DataGrid
          rows={pivotRows}
          columns={columnsWithClickableCells}
          getRowId={(row) => row.id}
          pageSizeOptions={[5, 10, 25]}
          onCellClick={handleCellClick}
          disableColumnMenu
          disableColumnFilter
          sx={{
            height: gridHeight,
          }}
        />
      )}

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 450, p: 2 } }}
        sx={{ "& .MuiDrawer-paper": { top: 64, height: "calc(100% - 64px)" } }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {asset === "truck" ? "Truck" : "Trailer"} details
            {selectedAuthority ? ` — ${selectedAuthority}` : ""}
          </Typography>

          <IconButton
            onClick={() => setDrawerOpen(false)}
            aria-label="Close asset details"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ mt: 2 }}>
          {isLoadingDrillDown ? (
            <CircularProgress />
          ) : drillDownError ? (
            <Typography color="error">{drillDownError}</Typography>
          ) : (
            <DetailsList data={drillDownData} asset={asset} />
          )}
        </Box>
      </Drawer>
    </Box>
  );
};

export default AuthorityTransactionTable;
